import styles from "../styles/applicationDetail.module.css";
import { CoResidentTabs } from "./CoResidentTabs";
import { ApplicationNinjaEquifaxScorePanel } from "./ApplicationNinjaEquifaxScorePanel";
import { ChecklistSidebar } from "./ChecklistSidebar";
import {
  RELATIONSHIP_TYPES,
  STATUS_MAP,
} from "../pages/applicationDetailHelpers";
import type { ApplicationDetailOverviewProps } from "../types";

export function ApplicationDetailOverview({
  applicantId,
  applicationAllData,
  applicationData,
  showChecklist,
  scoresInputsHidden,
  showUpdateNinjaEquifaxScoresButton,
  policyDeclarationAccessLoading,
  showDownloadAccessConfirmation,
  canImpersonate,
  canViewChecklist,
  ninjaScoreTogglePrimaryRef,
  ninjaScoreToggleCoRef,
  ninjaPanelPrimaryRef,
  ninjaPanelCoRef,
  onToggleChecklist,
  onRefresh,
  onCoResidentClick,
  onImpersonateClick,
  onNinjaEquifaxToggle,
  onReimbursementExplanation,
  onDownloadTerms,
  onNinjaEquifaxSaved,
}: ApplicationDetailOverviewProps) {
  const coresidents = applicationAllData.co_resident_application
    ? Object.values(applicationAllData.co_resident_application)
    : [];

  const overviewActions = (
    <>
      <button
        type="button"
        className={styles.impersonateButton}
        data-testid="application-detail-reimbursement-explanation-button"
        onClick={onReimbursementExplanation}
      >
        View Explanation of the Reimbursement Process
      </button>
      {showDownloadAccessConfirmation ? (
        <button
          type="button"
          className={styles.impersonateButton}
          data-testid="application-detail-download-access-confirmation-button"
          disabled={policyDeclarationAccessLoading}
          onClick={(e) =>
            void onDownloadTerms(e, { trackAccessConfirmationLoading: true })
          }
        >
          {policyDeclarationAccessLoading
            ? "Loading..."
            : "Download Access Confirmation"}
        </button>
      ) : null}
    </>
  );

  const ninjaPanel = (
    <ApplicationNinjaEquifaxScorePanel
      applicantId={applicantId}
      applicationData={applicationData as Record<string, unknown>}
      onSaved={onNinjaEquifaxSaved}
    />
  );

  return (
    <>
      {canViewChecklist && (
        <ChecklistSidebar
          showChecklist={showChecklist}
          onToggle={onToggleChecklist}
          applicantId={applicantId}
          applicationId={applicationAllData.application_id}
          applicationData={applicationData}
          onRefresh={onRefresh}
        />
      )}

      {coresidents.length > 0 && (
        <div
          className={styles.coResidentSection}
          data-testid="application-detail-co-resident-section"
        >
          <CoResidentTabs
            coresidents={coresidents}
            currentApplicantId={applicantId}
            onCoResidentClick={onCoResidentClick}
            relationshipTypes={RELATIONSHIP_TYPES}
            statusMap={STATUS_MAP}
            actions={
              <div
                className="flex w-full min-w-0 max-w-full flex-row flex-wrap items-center justify-end gap-2"
                data-testid="application-detail-co-resident-overview-actions"
              >
                {applicationData?.role_id ? (
                  <>
                    {canImpersonate && (
                      <button
                        type="button"
                        onClick={onImpersonateClick}
                        className={styles.impersonateButton}
                        data-testid={
                          showUpdateNinjaEquifaxScoresButton
                            ? "application-detail-coresident-impersonate-toolbar-button"
                            : "application-detail-coresident-impersonate-fab-button"
                        }
                      >
                        Impersonate
                      </button>
                    )}
                    {showUpdateNinjaEquifaxScoresButton ? (
                      <button
                        ref={ninjaScoreToggleCoRef}
                        type="button"
                        className={styles.impersonateButton}
                        data-testid="application-detail-coresident-ninja-equifax-toggle-button"
                        onClick={onNinjaEquifaxToggle}
                      >
                        {scoresInputsHidden
                          ? "Update Ninja/Equifax Score"
                          : "Hide Ninja/Equifax Score"}
                      </button>
                    ) : null}
                  </>
                ) : null}
                {overviewActions}
              </div>
            }
          />
          {applicationData?.role_id &&
            showUpdateNinjaEquifaxScoresButton &&
            !scoresInputsHidden && (
              <div
                id="ninja-equifax-score-panel-section"
                ref={ninjaPanelCoRef}
                data-testid="application-detail-ninja-equifax-panel-section-co"
                style={{
                  boxShadow: "0 4px 18px rgba(0, 0, 0, 0.14)",
                  borderRadius: 4,
                }}
              >
                {ninjaPanel}
              </div>
            )}
        </div>
      )}

      {!coresidents.length && applicationData?.role_id && (
        <div
          data-testid="application-detail-ninja-toolbar-primary"
          className={styles.ninjaToolbarPrimary}
        >
          {showUpdateNinjaEquifaxScoresButton ? (
            <>
              <button
                ref={ninjaScoreTogglePrimaryRef}
                type="button"
                className={styles.impersonateButton}
                data-testid="application-detail-primary-ninja-equifax-toggle-button"
                onClick={onNinjaEquifaxToggle}
              >
                {scoresInputsHidden
                  ? "Update Ninja/Equifax Score"
                  : "Hide Ninja/Equifax Score"}
              </button>
              {canImpersonate && (
                <button
                  type="button"
                  onClick={onImpersonateClick}
                  className={styles.impersonateButton}
                  data-testid="application-detail-primary-impersonate-toolbar-button"
                >
                  Impersonate
                </button>
              )}
            </>
          ) : (
            canImpersonate && (
              <button
                type="button"
                onClick={onImpersonateClick}
                className={styles.impersonateButton}
                data-testid="application-detail-primary-impersonate-button"
              >
                Impersonate
              </button>
            )
          )}
        </div>
      )}

      {showUpdateNinjaEquifaxScoresButton &&
        !coresidents.length &&
        !scoresInputsHidden && (
          <div
            id="ninja-equifax-score-panel-section"
            ref={ninjaPanelPrimaryRef}
            data-testid="application-detail-ninja-equifax-panel-section-primary"
            style={{
              boxShadow: "0 4px 18px rgba(0, 0, 0, 0.14)",
              borderRadius: 4,
            }}
          >
            {ninjaPanel}
          </div>
        )}

      {!coresidents.length && (
        <div
          className="mb-4 flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2"
          data-testid="application-detail-top-actions"
        >
          {overviewActions}
        </div>
      )}
    </>
  );
}
